// views/Dashboard/PainelExecutivo/components/SeletorPeriodoDashboard.tsx
import React, { useState } from 'react';
import type { PeriodoDashboard } from '../types';
import { OPCOES_PERIODO_PRESET } from '../types';
import type { PeriodoFiltro } from '../../../../src/lib/dashboard/periodoDashboard';

interface Props {
  periodo: PeriodoDashboard;
  onChange: (periodo: PeriodoDashboard) => void;
  /** Vai na MESMA linha do campo de período (ex.: o botão de comparar). No
   *  celular, fora desta linha ele descia sozinho e roubava uma linha inteira. */
  acessorio?: React.ReactNode;
}

type ModoPeriodo = 'preset' | 'mesEspecifico' | 'range';

const selectCls =
  'px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40';

const tabCls = (ativo: boolean) =>
  `px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
    ativo ? 'bg-primary text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
  }`;

// Extraído de FiltrosGlobaisDashboard.tsx pra ser reaproveitado duas vezes na
// mesma barra: Período (sempre visível) e Período de comparação (opcional,
// ver Task 3 deste plano).
const SeletorPeriodoDashboard: React.FC<Props> = ({ periodo, onChange, acessorio }) => {
  const [modo, setModo] = useState<ModoPeriodo>(periodo.tipo === 'preset' ? 'preset' : periodo.tipo);

  const trocarModo = (novoModo: ModoPeriodo) => {
    setModo(novoModo);
    if (novoModo === 'preset') onChange({ tipo: 'preset', preset: 'mesAtual' });
    if (novoModo === 'mesEspecifico') onChange({ tipo: 'mesEspecifico', mes: new Date().toISOString().slice(0, 7) });
    if (novoModo === 'range') {
      const hoje = new Date().toISOString().slice(0, 7);
      onChange({ tipo: 'range', inicio: hoje, fim: hoje });
    }
  };

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button type="button" className={tabCls(modo === 'preset')} onClick={() => trocarModo('preset')}>Período relativo</button>
        <button type="button" className={tabCls(modo === 'mesEspecifico')} onClick={() => trocarModo('mesEspecifico')}>Mês específico</button>
        <button type="button" className={tabCls(modo === 'range')} onClick={() => trocarModo('range')}>Período customizado</button>
      </div>

      {/* Campo + acessório na mesma linha. O campo estica no celular (flex-1) e
          o acessório não encolhe; se não couber (período customizado, com dois
          meses), o acessório quebra para a linha de baixo em vez de espremer. */}
      <div className="flex flex-wrap items-center gap-2 min-w-0">
      {modo === 'preset' && periodo.tipo === 'preset' && (
        <select
          className={`${selectCls} flex-1 min-w-0 sm:flex-none`}
          value={periodo.preset}
          onChange={(e) => onChange({ tipo: 'preset', preset: e.target.value as PeriodoFiltro })}
        >
          {OPCOES_PERIODO_PRESET.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {modo === 'mesEspecifico' && periodo.tipo === 'mesEspecifico' && (
        <input
          type="month"
          className={`${selectCls} flex-1 min-w-0 sm:flex-none`}
          value={periodo.mes}
          onChange={(e) => onChange({ tipo: 'mesEspecifico', mes: e.target.value })}
        />
      )}

      {modo === 'range' && periodo.tipo === 'range' && (
        <div className="flex items-center gap-2">
          <input
            type="month"
            className={selectCls}
            value={periodo.inicio}
            onChange={(e) => onChange({ tipo: 'range', inicio: e.target.value, fim: periodo.tipo === 'range' ? periodo.fim : e.target.value })}
          />
          <span className="text-gray-400 text-sm">até</span>
          <input
            type="month"
            className={selectCls}
            value={periodo.fim}
            onChange={(e) => onChange({ tipo: 'range', inicio: periodo.tipo === 'range' ? periodo.inicio : e.target.value, fim: e.target.value })}
          />
        </div>
      )}
      {acessorio && <div className="shrink-0">{acessorio}</div>}
      </div>
    </div>
  );
};

export default SeletorPeriodoDashboard;
